import type { AccountByIdApiResponseFull } from '../../api/apiSchema';

export interface Account {
    id: string;
    name: string;
    legalAddress: {
        line1?: string;
        line2?: string;
        line3?: string;
        city?: string;
        postalCode?: string;
        countryCode: string;
    };
    language?: string;
    taxId?: {
        value: string;
        taxType: string;
    };
    externalIdentifiers?: {
        type: string;
        value: string;
    }[];
    tenant: string;
    createdAt?: string;
    finalizationDate?: string;
    logisticsTenant?: string;
    tenantSpecificData?: {
        addressDetails?: string;
        cityId: string;
        neighbourhood: string;
        phoneNumber: string;
        state: string;
    };
    registrationChannel?: string;
    lifeCycleState?: string;
    accountType?: string;
    contacts?: {
        general?: {
            email: string;
        };
        billing?: {
            email: string;
        };
    };
}

export const mapToDomain = (restResource: AccountByIdApiResponseFull): Account => ({
    id: restResource.id,
    name: restResource.name,
    legalAddress: {
        line1: restResource.legal_address.line_1,
        line2: restResource.legal_address.line_2,
        line3: restResource.legal_address.line_3,
        city: restResource.legal_address.city,
        postalCode: restResource.legal_address.postal_code,
        countryCode: restResource.legal_address.country_code,
    },
    taxId: restResource.tax_id && {
        value: restResource.tax_id.value,
        taxType: restResource.tax_id.tax_type,
    },
    language: restResource.language,
    externalIdentifiers: restResource.external_identifiers,
    tenant: restResource.tenant,
    createdAt: restResource.created_at,
    finalizationDate: restResource.finalization_date,
    logisticsTenant: restResource.logistics_tenant,
    tenantSpecificData: restResource.tenant_specific_data && {
        addressDetails: restResource.tenant_specific_data.address_details,
        cityId: restResource.tenant_specific_data.city_id,
        neighbourhood: restResource.tenant_specific_data.neighbourhood,
        phoneNumber: restResource.tenant_specific_data.phone_number,
        state: restResource.tenant_specific_data.state,
    },
    registrationChannel: restResource.registration_channel,
    lifeCycleState: restResource.life_cycle_state,
    accountType: restResource.account_type,
    contacts: restResource.contacts,
});

export const mapToRest = (domain: Account): AccountByIdApiResponseFull => ({
    id: domain.id,
    name: domain.name,
    legal_address: {
        line_1: domain.legalAddress.line1,
        line_2: domain.legalAddress.line2,
        line_3: domain.legalAddress.line3,
        city: domain.legalAddress.city,
        postal_code: domain.legalAddress.postalCode,
        country_code: domain.legalAddress.countryCode,
    },
    tax_id: domain.taxId && {
        value: domain.taxId.value,
        tax_type: domain.taxId.taxType,
    },
    language: domain.language,
    external_identifiers: domain.externalIdentifiers,
    tenant: domain.tenant,
    created_at: domain.createdAt,
    finalization_date: domain.finalizationDate,
    logistics_tenant: domain.logisticsTenant,
    tenant_specific_data: domain.tenantSpecificData && {
        address_details: domain.tenantSpecificData.addressDetails,
        city_id: domain.tenantSpecificData.cityId,
        neighbourhood: domain.tenantSpecificData.neighbourhood,
        phone_number: domain.tenantSpecificData.phoneNumber,
        state: domain.tenantSpecificData.state,
    },
    registration_channel: domain.registrationChannel,
    life_cycle_state: domain.lifeCycleState,
    account_type: domain.accountType,
    contacts: domain.contacts,
});

// FORM VALIDATION
export const formFieldName = {
    // ID: 'id',
    NAME: 'name',
    // TENANT: 'tenant',
    // CHANNEL: 'registrationChannel',
    TAXID_VALUE: 'taxId_value',
    // TAXID_TYPE: 'taxId_taxType',
    ADDRESS_LINE1: 'legalAddress_line1',
    ADDRESS_LINE2: 'legalAddress_line2',
    // ADDRESS_LINE3: 'legalAddress_line3',
    ADDRESS_CITY: 'legalAddress_city',
    ADDRESS_POST_CODE: 'legalAddress_postCode',
    ADDRESS_COUNTRY_CODE: 'legalAddress_country',
    CONTACTS_GENERAL: 'contacts_general',
    CONTACTS_BILLING: 'contacts_billing',
    LANGUAGE: 'language',
    // LIFECYCLESTATE: 'lifeCycleState',
    // TENANT_CITY_ID: 'tenantSpecificData_cityId',
    // TENANT_NEIGHBOURHOOD: 'tenantSpecificData_neighbourhood',
    // TENANT_PHONE_NUMBER: 'tenantSpecificData_phoneNumber',
    // TENANT_STATE: 'tenantSpecificData_state',
    // TENANT_ADDRESS_DETAILS: 'tenantSpecificData_addressDetails',
} as const;

export type FormFieldName = (typeof formFieldName)[keyof typeof formFieldName];
